import React, {CSSProperties} from 'react';
import classnames from 'classnames';
import {SplitPane} from '../splitpane/SplitPane';

/**
 * {@docCategory VerticalSplitPanel}
 */
export interface VerticalSplitPanelProps {
   /**
    * Array of default sizes for the individual panes. Missing values default to 1. When
    * no defaultSizes are passed, all sizes default to 1, equally distributing the available space
    * (as far as minSize values permit).  If the value is a string and ends with px, it is treated
    * as a pixel value rather than a flex value.
    */
   defaultSizes?: (string | number)[];

   /**
    * Minimum size of all panes (in pixels), or array containing individual minimum sizes for each
    * pane.
    * @defaultvalue 50
    */
   minSize?: number | number[];

   /**
    * Maximum size of all panes (in pixels), or array containing individual maximum sizes for each
    * pane.  A -1 indicates no maximum size
    * @defaultvalue -1
    */
   maxSize?: number | number[];

   /**
    * Used to temporarily disable resizing on a resizer index.  If there are two regions, there will
    * be only one resizer index, which starts at 0.
    */
   disableResizers?: number | number[];

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * This callback is invoked when a drag starts.
    */
   onDragStarted?: () => void;

   /**
    * This callback is invoked when a drag ends.
    *
    * @param sizes - The sizes of each child panel
    */
   onDragFinished?: (sizes: number[]) => void;

   /**
    * This callback is invoked with the current drag during a drag event. It is recommended that it
    * is wrapped in a debounce function.
    *
    * @param sizes - The sizes of each child panel
    */
   onChange?: (sizes: number[]) => void;

   /**
    * The child panels
    */
   children: React.ReactChild[];
}

export const VerticalSplitPanel = (props: VerticalSplitPanelProps) => {

   return (
      <SplitPane
         split={'horizontal'}
         defaultSizes={props.defaultSizes}
         minSize={props.minSize}
         maxSize={props.maxSize}
         disableResizers={props.disableResizers}
         onDragStarted={props.onDragStarted}
         onDragFinished={props.onDragFinished}
         onChange={props.onChange}
         className={classnames('uf-vertical-split-panel', props.className)}
         style={props.style}
      >
         {props.children}
      </SplitPane>
   );
};
